import React from 'react';
import { CouponCode, LineItemInfo, Price, Button } from '@components';
import { Typography, Icon } from 'gd-design-library';
import { Cart as CartInterface, DiscountCode } from '@services/cart';
import { OrderExpanded } from '@services/checkout';
import classNames from 'classnames/bind';
import styles from './Cart.module.scss';
import { DiscountCodeReference } from '@commercetools/platform-sdk';
import { CartOutOfStockItems } from '@services/cart/interfaces';

const CN = 'cart';
const titleClassName = `${CN}__title`;
const titleRowClassName = `${CN}__title-row`;
const orderNumberClassName = `${CN}__order-number`;
const discountsAndShippingClassName = `${CN}__discounts-and-shipping`;
const shippingClassName = `${CN}__shipping`;
const shippingTitleClassName = `${shippingClassName}__title`;
const shippingIconClassName = `${shippingClassName}__icon`;
const shippingPriceClassName = `${shippingClassName}__price`;
const totalClassName = `${CN}__total`;
const totalPriceClassName = `${totalClassName}__price`;
const closeBtnClassName = `${CN}__close-btn`;

const cx = classNames.bind(styles);

interface Props {
  cart: CartInterface | OrderExpanded | null;
  onItemQuantityChange: (lineItemId: string, newQuantity: number) => unknown;
  onLineItemRemove: (lineItemId: string) => unknown;
  onAddDiscountCode: (discountCode: string) => Promise<void>;
  onRemoveDiscountCode: (discountCode: DiscountCodeReference) => Promise<void>;
  className?: string;
  isLoadingDiscount: boolean;
  isLoadingLineItems?: boolean;
  readOnly?: boolean;
  onClose?: () => void;
  outOfStockItems?: CartOutOfStockItems[];
  title?: string;
  orderNumber?: string;
  hidePromoCode?: boolean;
}

export const Cart: React.FC<Props> = ({
  cart,
  onItemQuantityChange,
  onLineItemRemove,
  onAddDiscountCode,
  onRemoveDiscountCode,
  className,
  isLoadingDiscount,
  isLoadingLineItems,
  readOnly = false,
  onClose,
  outOfStockItems = [],
  title = 'Your Cart',
  orderNumber,
  hidePromoCode = false
}) => (
  <>
    <div className={ cx(titleRowClassName) }>
      <div>
        <Typography variant='h4' className={ cx(titleClassName) }>{title}</Typography>
        {orderNumber && (
          <Typography variant='caption' as='span' className={ cx(orderNumberClassName) }>
            {orderNumber}
          </Typography>
        )}
      </div>
      {!!onClose && (
        <Button
          isIcon
          aria-label='Close cart'
          onClick={ onClose }
          variant='inherit'
          color='secondary'
          className={ cx(closeBtnClassName) }
        >
          <Icon name='cross' />
        </Button>
      )}
    </div>
    <div className={ cx(CN, className) }>
      {!cart?.lineItems.length ? (
        <Typography variant='h6'>You have no items in cart.</Typography>
      ) : (
        <>
          {cart.lineItems.map(lineItem => (
            <LineItemInfo
              lineItem={ lineItem }
              onItemQuantityChange={ onItemQuantityChange }
              onLineItemRemove={ onLineItemRemove }
              disabled={ isLoadingLineItems }
              readOnly={ readOnly }
              key={ lineItem.id }
              isOutOfStock={ outOfStockItems.some(item => item.id === lineItem.id) }
              availableQuantity={ outOfStockItems.find(item => item.id === lineItem.id)?.availableQuantity }
            />
          ))}
          <div className={ cx(discountsAndShippingClassName) }>
            {!hidePromoCode && (
              <CouponCode
                isLoading={ isLoadingDiscount }
                discountCodes={ cart.discountCodes as DiscountCode[] | undefined }
                removeDiscountCode={ onRemoveDiscountCode }
                addDiscountCode={ onAddDiscountCode }
              />
            )}
            <div className={ cx(shippingClassName) }>
              <Typography className={ cx(shippingTitleClassName) } variant='h6'>
                <span className={ cx(shippingIconClassName) }>
                  <Icon name='localShipping' />
                </span>
                {`Shipping${!cart.shippingPrice ? ' calculated on next step' : ''}`}
              </Typography>
              {cart.shippingPrice && (
                <Price className={ cx(shippingPriceClassName) } price={ cart.shippingPrice } />
              )}
            </div>
          </div>
          <div className={ cx(totalClassName) }>
            <Typography variant='h4'>Total</Typography>
            <Price className={ cx(totalPriceClassName) } price={ cart.totalPrice } />
          </div>
        </>
      )}
    </div>
  </>
);
